// Loads the Google Maps JavaScript API exactly once, however many map
// components mount concurrently (Dashboard + Live Map + a detail page can
// all want a map in the same session).
let loaderPromise = null;

export function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (loaderPromise) return loaderPromise;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set"));
  }

  loaderPromise = new Promise((resolve, reject) => {
    window.__arcGoogleMapsReady = () => resolve(window.google.maps);

    const script = document.createElement("script");
    // `geometry` + `places`, and deliberately NOT `drawing`. Each library is
    // fetched as its own async script alongside main.js while the `callback`
    // fires as soon as main.js alone is ready — so a library can still be
    // undefined at that moment. The geofence draw tool therefore uses the core
    // API instead of `drawing` (see MapCanvas's notes), and anything touching
    // `places` must go through waitForPlacesLibrary() below rather than
    // assuming google.maps.places exists.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=geometry,places&loading=async&callback=__arcGoogleMapsReady`;
    script.async = true;
    script.onerror = () => {
      loaderPromise = null; // allow a retry (e.g. after the user fixes the key) instead of caching a dead promise
      reject(new Error("Failed to load the Google Maps script"));
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
}

// Resolves with google.maps.places once its own script has landed, or with
// null if it never does (script blocked, or Places API not enabled on the
// key). Never rejects: callers treat suggestions as an enhancement and fall
// back to the core-API Geocoder, so a null here must not break the feature.
export function waitForPlacesLibrary(timeoutMs = 8000) {
  const ready = () => window.google?.maps?.places?.AutocompleteService || null;
  if (ready()) return Promise.resolve(window.google.maps.places);

  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (ready()) return resolve(window.google.maps.places);
      if (Date.now() > deadline) return resolve(null);
      setTimeout(tick, 120);
    };
    tick();
  });
}

